<template>
  <v-dialog :value="true" fullscreen>
    <div class="fill-height d-flex flex-column chat-bg dark-theme">
      <div class="bg-orbs">
        <div class="orb orb-1"></div>
        <div class="orb orb-2"></div>
      </div>

      <v-toolbar color="rgba(18,18,18,0.85)" dark elevation="0" class="flex-grow-0 w-100 toolbar-glass" height="72">
        <v-btn v-if="selectedStudio" icon @click="resetStudio" class="mr-2 back-btn">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn v-if="!selectedStudio" icon @click="$router.push('/dashboard')" class="mr-2 back-btn">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-avatar tile size="42" class="mr-3 avatar-pulse">
          <v-img contain src="/img/roody_logo.png"></v-img>
        </v-avatar>
        <div>
          <v-toolbar-title class="font-weight-black text-subtitle-1 mb-n1 text-uppercase letter-spacing-1"> Roody · dein Coach </v-toolbar-title>
          <div v-if="selectedStudio" class="caption d-flex align-center status-line">
            <span class="status-dot mr-2"></span>
            <v-icon x-small color="grey lighten-1" class="mr-1">mdi-map-marker</v-icon>
            {{ selectedStudio.name }} · online
          </div>
        </div>
      </v-toolbar>

      <transition name="fade-slide" mode="out-in">
        <!-- ============ STUDIO SELECT ============ -->
        <div v-if="!selectedStudio" key="select" class="flex-grow-1 overflow-y-auto pa-4 pa-md-8 d-flex flex-column relative-z">
          <div class="text-center mb-8 mt-4 fade-in-up">
            <h1 class="text-h4 font-weight-black white--text mb-2 text-uppercase letter-spacing-1">
              Wähle dein <span class="grad-text">Studio</span>
            </h1>
            <p class="grey--text text-body-1">Wo möchtest du trainieren? Roody berät dich gerne.</p>
          </div>

          <div class="d-flex flex-column align-center w-100 mx-auto fade-in-up delay-1" style="max-width: 800px">
            <v-text-field
              v-model="search"
              dark
              rounded
              filled
              append-icon="mdi-magnify"
              placeholder="Suche nach Kanton, Stadt oder PLZ..."
              hide-details
              clearable
              color="primary"
              background-color="rgba(30,30,30,0.7)"
              class="w-100 mb-6 custom-search"></v-text-field>
            <div class="d-flex flex-wrap justify-center mb-8">
              <v-chip
                v-for="canton of studioCantons"
                :key="canton"
                dark
                :color="selectedCanton === canton ? 'primary' : 'rgba(30,30,30,0.7)'"
                @click="selectedCanton = selectedCanton === canton ? null : canton"
                class="ma-1 font-weight-bold canton-chip"
                :class="{ 'canton-active': selectedCanton === canton }">
                {{ canton }}
              </v-chip>
            </div>
          </div>

          <div v-if="loadingStudios" class="d-flex justify-center my-12">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
          </div>
          <div v-else class="d-flex flex-wrap justify-center mx-auto fade-in-up delay-2" style="max-width: 1200px; gap: 20px">
            <div
              v-for="(studio, idx) of filteredStudios"
              :key="studio._id"
              class="stagger-in"
              :style="{ animationDelay: idx * 60 + 'ms', width: '100%', maxWidth: '350px' }">
              <v-card dark class="d-flex flex-column fill-height card-glass-dark" @click="selectStudio(studio)" v-ripple>
                <div class="pa-6 d-flex flex-column flex-grow-1 text-center align-center">
                  <v-img
                    height="100"
                    contain
                    :src="`https://app.wellcomefit.ch/img/logos/studio/WELL-COME-FIT_LOGO_${studio.code.toUpperCase()}_BLACK.png`"
                    class="mb-2"></v-img>

                  <v-card-title class="text-h6 font-weight-black pa-0 mb-2 text-uppercase white--text">{{ studio.name }}</v-card-title>
                  <v-card-subtitle class="pa-0 grey--text" style="font-size: 0.85rem">
                    <v-icon x-small color="grey" class="mr-1">mdi-map-marker-outline</v-icon>
                    {{ studio.adresse || "Adresse nicht verfügbar" }}
                  </v-card-subtitle>
                  <v-spacer></v-spacer>
                  <v-btn rounded color="primary" class="mt-6 font-weight-bold px-6 shadow-glow w-100" elevation="0">
                    Chat Starten <v-icon right>mdi-message-text-outline</v-icon>
                  </v-btn>
                </div>
              </v-card>
            </div>
          </div>
        </div>

        <!-- ============ CHAT ============ -->
        <div v-else key="chat" class="flex-grow-1 d-flex flex-column overflow-hidden relative relative-z">
          <div class="flex-grow-1 w-100 overflow-y-auto hide-scrollbar smooth-scroll chat-scroll-outer" ref="chatBox">
            <div class="chat-inner pa-4">
              <!-- Welcome -->
              <div class="d-flex mb-4 flex-column align-start mt-4 message-enter">
                <div class="d-flex flex-row align-end">
                  <v-avatar size="38" class="mx-2 roody-avatar">
                    <v-img src="/img/roody_logo.png"></v-img>
                  </v-avatar>
                  <div class="pa-4 rounded-2xl text-body-1 ai-bubble" style="max-width: 85%">
                    Hey! 👋 Willkommen im <strong>well come FIT {{ selectedStudio.name }}</strong
                    >.<br /><br />
                    Ich bin <strong class="primary--text">Roody</strong>, dein persönlicher Coach. Ich helfe dir, das passende Abo zu finden – ehrlich
                    und auf dich zugeschnitten. Womit kann ich starten? 💪
                  </div>
                </div>

                <div class="quick-actions ml-12 mt-3">
                  <v-chip
                    v-for="(qa, i) in quickActions"
                    :key="i"
                    class="qa-chip ma-1"
                    @click="sendQuickAction(qa.text)"
                    :style="{ animationDelay: 200 + i * 80 + 'ms' }">
                    <v-icon color="white" left small>{{ qa.icon }}</v-icon
                    >{{ qa.label }}
                  </v-chip>
                </div>
              </div>

              <transition-group name="msg-list" tag="div">
                <div
                  v-for="(msg, index) in messages"
                  :key="'m-' + index"
                  class="d-flex mb-6 flex-column"
                  :class="msg.role === 'user' ? 'align-end' : 'align-start'">
                  <div class="d-flex" :class="msg.role === 'user' ? 'flex-row-reverse' : 'flex-row align-end'">
                    <v-avatar v-if="msg.role === 'assistant'" size="38" class="mx-2 roody-avatar">
                      <v-img src="/img/roody_logo.png"></v-img>
                    </v-avatar>
                    <div
                      class="pa-3 px-4 rounded-2xl text-body-1 message-bubble"
                      :class="msg.role === 'user' ? 'user-bubble' : 'ai-bubble'"
                      style="max-width: 85%; white-space: pre-wrap">
                      <v-img v-if="msg.image" :src="msg.image" max-width="250" class="rounded-lg mb-2 border-highlight"></v-img>
                      <span v-html="formatMessage(msg.content)"></span>
                    </div>
                  </div>

                  <!-- ============ PRICES ============ -->
                  <transition name="widget-pop">
                    <div
                      v-if="msg.widgetType === 'prices' && msg.widgetData && msg.widgetData.length"
                      class="mt-3 ml-2 ml-md-12 widget-wrap widget-wide">
                      <div class="widget-label">
                        <v-icon x-small color="primary" class="mr-1">mdi-tag-multiple</v-icon>
                        {{ msg.recommendedTitle && !msg.expanded ? "Empfehlung für dich" : msg.widgetData.length + " Abos · tippe für Details" }}
                      </div>

                      <!-- Einzel-Empfehlung (gross) -->
                      <div v-if="msg.recommendedTitle && !msg.expanded">
                        <v-card dark class="price-card-featured rounded-2xl" @click="openPriceDetail(msg.widgetData[0])">
                          <div class="featured-badge-big">⭐ UNSERE EMPFEHLUNG</div>
                          <v-card-text class="pa-6">
                            <div class="abo-title-big">{{ msg.widgetData[0].title }}</div>
                            <div class="abo-desc mb-4">{{ msg.widgetData[0].description }}</div>

                            <div v-if="priceCalc(msg.widgetData[0], msg)" class="featured-variant">
                              <div class="d-flex align-baseline flex-wrap" style="gap: 8px">
                                <span class="grey--text">Laufzeit</span>
                                <span class="white--text font-weight-bold">{{ priceCalc(msg.widgetData[0], msg).variant.label }}</span>
                              </div>
                              <div class="d-flex align-baseline mt-3 flex-wrap" style="gap: 4px">
                                <span class="detail-price">CHF {{ priceCalc(msg.widgetData[0], msg).variant.price }}</span>
                                <span class="grey--text caption ml-1">Abo gesamt</span>
                              </div>
                              <div class="caption grey--text mt-1">
                                entspricht CHF {{ priceCalc(msg.widgetData[0], msg).variant.monthly }} / Monat
                              </div>

                              <!-- NEU: Addons -->
                              <div v-if="priceCalc(msg.widgetData[0], msg).matchedAddons.length" class="addons-block mt-3">
                                <div class="addons-label">Empfohlene Zusatzpakete (pro Laufzeit):</div>
                                <div
                                  v-for="a in priceCalc(msg.widgetData[0], msg).matchedAddons"
                                  :key="a.name"
                                  class="d-flex justify-space-between addon-row">
                                  <span>+ {{ a.name }}</span>
                                  <span class="primary--text font-weight-bold">CHF {{ a.price }}</span>
                                </div>
                              </div>

                              <!-- Aktivierungsgebühr -->
                              <div v-if="priceCalc(msg.widgetData[0], msg).originalFee" class="d-flex justify-space-between addon-row mt-2">
                                <span>+ Aktivierung einmalig</span>
                                <span
                                  :class="
                                    priceCalc(msg.widgetData[0], msg).feeWaived
                                      ? 'orange--text text-decoration-line-through'
                                      : 'primary--text font-weight-bold'
                                  ">
                                  CHF {{ priceCalc(msg.widgetData[0], msg).originalFee }}
                                </span>
                              </div>
                              <div v-if="priceCalc(msg.widgetData[0], msg).feeWaived" class="caption orange--text mt-1">
                                ✓ Aktivierungsgebühr entfällt (Zusatzpakete ≥ CHF 100)
                              </div>

                              <!-- Total -->
                              <v-divider class="my-3 grey darken-3"></v-divider>
                              <div class="d-flex justify-space-between align-baseline">
                                <span class="white--text font-weight-bold text-uppercase" style="letter-spacing: 1px; font-size: 12px"
                                  >Dein Total</span
                                >
                                <span class="detail-price">CHF {{ priceCalc(msg.widgetData[0], msg).total }}</span>
                              </div>
                            </div>
                            <div class="abo-cta-hint mt-4"><v-icon x-small>mdi-gesture-tap</v-icon> Alle Laufzeiten & Zusatzpakete</div>
                          </v-card-text>
                        </v-card>
                        <div class="d-flex flex-wrap mt-3" style="gap: 8px">
                          <v-btn rounded color="primary" class="shadow-glow font-weight-bold" :href="msg.widgetData[0].contractUrl" target="_blank">
                            <v-icon left small>mdi-rocket-launch</v-icon> Online abschliessen
                          </v-btn>
                          <v-btn v-if="msg.widgetAllData && msg.widgetAllData.length > 1" rounded outlined color="primary" @click="expandPrices(msg)">
                            <v-icon left small>mdi-view-grid-outline</v-icon> Weitere Abos anzeigen
                          </v-btn>
                        </div>
                      </div>

                      <!-- Alle Abos Carousel -->
                      <div v-else>
                        <div class="d-flex overflow-x-auto hide-scrollbar pb-3 pt-2 pl-1 pr-2" style="gap: 14px; scroll-snap-type: x mandatory">
                          <v-card
                            v-for="(abo, i) in msg.widgetData"
                            :key="abo.id"
                            dark
                            class="price-card-compact rounded-2xl"
                            :class="{ featured: abo.title === 'Kombi Card' }"
                            style="min-width: 230px; max-width: 230px; flex: 0 0 auto; scroll-snap-align: start"
                            @click="openPriceDetail(abo)">
                            <div v-if="abo.title === 'Kombi Card'" class="featured-badge">⭐ TOP</div>
                            <v-card-text class="pa-4 d-flex flex-column" style="height: 100%">
                              <div class="abo-title">{{ abo.title }}</div>
                              <div class="abo-desc">{{ abo.description }}</div>
                              <v-spacer></v-spacer>
                              <div class="abo-price-wrap mt-3">
                                <div class="abo-from">ab</div>
                                <div class="abo-price">CHF {{ abo.fromMonthly }}</div>
                                <div class="abo-unit">/ Monat</div>
                              </div>
                              <div class="abo-cta-hint"><v-icon x-small>mdi-gesture-tap</v-icon> Details ansehen</div>
                            </v-card-text>
                          </v-card>
                        </div>
                        <v-btn
                          rounded
                          color="primary"
                          class="mt-2 ml-1 shadow-glow font-weight-bold"
                          :href="msg.widgetData[0] && msg.widgetData[0].contractUrl"
                          target="_blank">
                          <v-icon left small>mdi-rocket-launch</v-icon> Jetzt online abschliessen
                        </v-btn>
                      </div>
                    </div>
                  </transition>

                  <!-- ============ STUDIO INFO ============ -->
                  <transition name="widget-pop">
                    <div
                      v-if="msg.widgetType === 'info' && msg.widgetData"
                      class="mt-3 ml-2 ml-md-12 widget-wrap"
                      style="width: 100%; max-width: 520px">
                      <v-card dark class="info-card-compact rounded-2xl" @click="openInfoDetail(msg.widgetData)">
                        <div class="info-header-compact pa-4">
                          <div class="d-flex align-center justify-space-between">
                            <div class="d-flex align-center">
                              <v-icon color="primary" class="mr-2">mdi-map-marker-radius</v-icon>
                              <div class="white--text font-weight-black text-uppercase">{{ msg.widgetData.name }}</div>
                            </div>
                            <v-chip v-if="msg.widgetData.alwaysCheckin" x-small color="green darken-1" text-color="white" class="font-weight-black">
                              <v-icon x-small left>mdi-door-open</v-icon> 24/7
                            </v-chip>
                          </div>
                          <div class="caption grey--text mt-1">{{ msg.widgetData.address }}</div>
                        </div>
                        <v-card-text class="py-3">
                          <div class="info-row">
                            <v-icon x-small color="primary" class="mr-2">mdi-phone</v-icon>
                            <span class="caption white--text">{{ msg.widgetData.phone }}</span>
                          </div>
                          <div class="info-row">
                            <v-icon x-small color="primary" class="mr-2">mdi-email-outline</v-icon>
                            <span class="caption white--text">{{ msg.widgetData.email }}</span>
                          </div>
                          <div v-if="todayHours(msg.widgetData)" class="info-row">
                            <v-icon x-small color="primary" class="mr-2">mdi-clock-outline</v-icon>
                            <span class="caption white--text">Heute: {{ todayHours(msg.widgetData) }}</span>
                          </div>
                          <div v-if="msg.widgetData.features && msg.widgetData.features.length" class="mt-2 d-flex flex-wrap" style="gap: 4px">
                            <v-chip v-for="(f, i) in msg.widgetData.features.slice(0, 5)" :key="i" x-small class="feature-chip-mini">{{ f }}</v-chip>
                            <v-chip v-if="msg.widgetData.features.length > 5" x-small class="feature-chip-mini"
                              >+{{ msg.widgetData.features.length - 5 }}</v-chip
                            >
                          </div>
                        </v-card-text>
                        <div class="info-tap-hint"><v-icon x-small>mdi-gesture-tap</v-icon> Tippen für alle Öffnungszeiten & Ausstattung</div>
                      </v-card>
                    </div>
                  </transition>

                  <!-- ============ COURSES ============ -->
                  <transition name="widget-pop">
                    <div
                      v-if="msg.widgetType === 'courses' && msg.widgetData && msg.widgetData.length"
                      class="mt-3 ml-2 ml-md-12 widget-wrap"
                      style="width: 100%; max-width: 600px">
                      <!-- ===== SUGGESTIONS MODE: kuratierte Kurs-Vorschläge ===== -->
                      <v-card v-if="msg.widgetMode === 'suggestions'" dark class="course-card rounded-2xl">
                        <div class="pa-4 pb-2 d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="orange" class="mr-2">mdi-star-circle</v-icon>
                            <span class="primary--text font-weight-bold text-uppercase letter-spacing-1">
                              Kurs-Vorschläge ({{ msg.widgetData.length }})
                            </span>
                          </div>
                        </div>
                        <div class="px-4 pb-4 suggestion-list">
                          <div v-for="kurs in msg.widgetData" :key="kurs.id" class="suggestion-card">
                            <div class="d-flex align-start justify-space-between mb-2 flex-wrap" style="gap: 8px">
                              <div class="suggestion-title">{{ kurs.courseName }}</div>
                              <v-chip x-small color="rgba(25,118,210,0.2)" class="suggestion-time-chip">
                                <v-icon x-small left>mdi-clock-outline</v-icon>{{ kurs.time }}
                              </v-chip>
                            </div>
                            <div class="suggestion-meta">
                              <v-icon x-small color="primary" class="mr-1">mdi-calendar</v-icon>
                              <span>{{ kurs.day }}, {{ kurs.date.split("-").reverse().join(".") }}</span>
                              <span class="mx-2">·</span>
                              <v-icon x-small color="primary" class="mr-1">mdi-account</v-icon>
                              <span>{{ kurs.instructors }}</span>
                              <template v-if="kurs.room">
                                <span class="mx-2">·</span>
                                <v-icon x-small color="primary" class="mr-1">mdi-door</v-icon>
                                <span>{{ kurs.room }}</span>
                              </template>
                            </div>
                            <div v-if="kurs.description" class="suggestion-desc">{{ kurs.description }}</div>
                          </div>
                        </div>
                      </v-card>

                      <!-- ===== FULL MODE: ganzer Kursplan ===== -->
                      <v-card v-else dark class="course-card rounded-2xl">
                        <div class="pa-4 pb-2 d-flex align-center justify-space-between">
                          <div class="d-flex align-center">
                            <v-icon color="primary" class="mr-2">mdi-calendar-clock</v-icon>
                            <span class="primary--text font-weight-bold text-uppercase letter-spacing-1">
                              Kurse ({{ filteredCourseCount(msg) }})
                            </span>
                          </div>
                        </div>

                        <!-- Highlight Kurs (Legacy) -->
                        <div v-if="msg.highlightCourse" class="px-4 pb-2">
                          <div class="highlight-course-card">
                            <div class="d-flex align-center mb-1">
                              <v-icon x-small color="orange" class="mr-1">mdi-star</v-icon>
                              <span class="highlight-label">Empfohlen</span>
                            </div>
                            <div class="white--text font-weight-black">{{ msg.highlightCourse.courseName }}</div>
                            <div class="caption primary--text">
                              {{ msg.highlightCourse.day }} · {{ msg.highlightCourse.time }} · {{ msg.highlightCourse.instructors }}
                            </div>
                            <div v-if="msg.highlightCourse.description" class="caption grey--text mt-2">{{ msg.highlightCourse.description }}</div>
                          </div>
                        </div>

                        <div class="px-4 pb-2 d-flex flex-wrap" style="gap: 6px">
                          <v-chip
                            x-small
                            :color="msg.courseFilter === 'all' ? 'primary' : 'rgba(255,255,255,0.08)'"
                            @click="$set(msg, 'courseFilter', 'all')"
                            class="day-filter-chip"
                            >Alle</v-chip
                          >
                          <v-chip
                            v-for="day in uniqueDays(msg.widgetData)"
                            :key="day"
                            x-small
                            :color="msg.courseFilter === day ? 'primary' : 'rgba(255,255,255,0.08)'"
                            @click="$set(msg, 'courseFilter', day)"
                            class="day-filter-chip"
                            >{{ day.split(",")[0] }}</v-chip
                          >
                        </div>
                        <div class="course-scroll hide-scrollbar">
                          <div v-for="(group, day) in groupedCoursesFiltered(msg)" :key="day" class="course-day-group">
                            <div class="day-header">{{ day }}</div>
                            <div
                              v-for="kurs in group"
                              :key="kurs.id"
                              class="course-row"
                              :class="{ 'course-row-highlight': msg.highlightCourse && msg.highlightCourse.id === kurs.id }">
                              <div class="course-time">{{ kurs.time }}</div>
                              <div class="course-info">
                                <div class="white--text font-weight-bold">{{ kurs.courseName }}</div>
                                <div class="caption grey--text">
                                  {{ kurs.instructors }}<span v-if="kurs.room"> · {{ kurs.room }}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </transition>
                  <!-- ============ TRIAL FORM ============ -->
                  <transition name="widget-pop">
                    <div
                      v-if="msg.widgetType === 'trial' && msg.widgetData"
                      class="mt-3 ml-2 ml-md-12 widget-wrap"
                      style="width: 100%; max-width: 520px">
                      <v-card dark class="trial-card rounded-2xl">
                        <div class="trial-header pa-5">
                          <div class="d-flex align-center mb-2">
                            <v-icon color="orange" class="mr-2">mdi-gift-outline</v-icon>
                            <span class="trial-title">11 TAGE GRATIS TESTEN</span>
                          </div>
                          <div class="caption grey--text">Kein Abo, kein Risiko. Nur deine Daten und du kannst loslegen.</div>
                        </div>
                        <v-divider class="grey darken-3"></v-divider>

                        <!-- SUCCESS STATE -->
                        <div v-if="msg.widgetData.submitted" class="pa-6 text-center">
                          <v-icon color="green" size="64" class="mb-3">mdi-check-circle</v-icon>
                          <div class="white--text font-weight-bold text-h6 mb-2">Geschafft! 🎉</div>
                          <div class="caption grey--text">Wir melden uns in Kürze bei dir, um deinen Start zu organisieren.</div>
                        </div>

                        <!-- FORM STATE -->
                        <v-card-text v-else class="pa-5">
                          <v-text-field
                            v-model="msg.widgetData.firstName"
                            dark
                            dense
                            outlined
                            hide-details
                            label="Vorname *"
                            color="primary"
                            background-color="rgba(255,255,255,0.03)"
                            class="mb-3"></v-text-field>
                          <v-text-field
                            v-model="msg.widgetData.lastName"
                            dark
                            dense
                            outlined
                            hide-details
                            label="Nachname *"
                            color="primary"
                            background-color="rgba(255,255,255,0.03)"
                            class="mb-3"></v-text-field>
                          <v-text-field
                            v-model="msg.widgetData.phone"
                            dark
                            dense
                            outlined
                            hide-details
                            label="Handynummer *"
                            type="tel"
                            prepend-inner-icon="mdi-phone-outline"
                            color="primary"
                            background-color="rgba(255,255,255,0.03)"
                            class="mb-3"></v-text-field>
                          <v-text-field
                            v-model="msg.widgetData.email"
                            dark
                            dense
                            outlined
                            hide-details
                            label="E-Mail (optional)"
                            type="email"
                            prepend-inner-icon="mdi-email-outline"
                            color="primary"
                            background-color="rgba(255,255,255,0.03)"
                            class="mb-3"></v-text-field>
                          <v-textarea
                            v-model="msg.widgetData.message"
                            dark
                            dense
                            outlined
                            hide-details
                            label="Nachricht (optional)"
                            rows="2"
                            color="primary"
                            background-color="rgba(255,255,255,0.03)"></v-textarea>

                          <v-alert dense text type="info" color="primary" class="mt-3 caption" icon="mdi-information-outline">
                            Nur für <strong>Neukunden</strong>. Zutritt während der 11 Tage zu den <strong>betreuten Zeiten</strong>.
                          </v-alert>

                          <div v-if="msg.widgetData.error" class="caption red--text mt-2">
                            {{ msg.widgetData.error }}
                          </div>

                          <v-btn
                            block
                            rounded
                            color="primary"
                            large
                            class="mt-4 shadow-glow font-weight-bold"
                            :loading="msg.widgetData.loading"
                            :disabled="!trialFormValid(msg.widgetData)"
                            @click="submitTrialLead(msg)">
                            <v-icon left>mdi-rocket-launch</v-icon>
                            11 Tage sichern
                          </v-btn>
                        </v-card-text>
                      </v-card>
                    </div>
                  </transition>
                </div>
              </transition-group>

              <div v-if="isTyping" class="d-flex mb-4 justify-start align-center ml-12 mt-2 typing-enter">
                <div class="pa-3 px-4 rounded-2xl ai-bubble d-flex align-center">
                  <span class="typing-dot"></span><span class="typing-dot mx-1"></span><span class="typing-dot"></span>
                </div>
              </div>
            </div>
          </div>

          <!-- INPUT BAR -->
          <v-card color="rgba(18,18,18,0.9)" class="w-100 flex-grow-0 rounded-0 input-bar pa-3" dark elevation="0">
            <div class="chat-inner">
              <v-slide-y-transition>
                <div v-if="selectedImagePreview" class="d-flex align-end mb-3">
                  <div style="position: relative; display: inline-block">
                    <v-img :src="selectedImagePreview" max-width="80" max-height="80" class="rounded-lg border-highlight"></v-img>
                    <v-btn icon x-small color="red" class="remove-img-btn" @click="removeSelectedImage">
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-slide-y-transition>
              <div class="d-flex align-end">
                <v-btn icon @click="$refs.imageInput.click()" class="mb-1 mr-2 image-btn" :color="selectedImagePreview ? 'primary' : 'grey'">
                  <v-icon>mdi-image-outline</v-icon>
                </v-btn>
                <input type="file" ref="imageInput" class="d-none" accept="image/*" @change="onImageSelected" />
                <v-text-field
                  v-model="inputText"
                  dark
                  placeholder="Frag Roody..."
                  outlined
                  dense
                  rounded
                  auto-grow
                  rows="1"
                  hide-details
                  color="primary"
                  background-color="rgba(30,30,30,0.8)"
                  @keydown.enter.prevent="sendMessage"
                  class="custom-chat-input"></v-text-field>
                <v-btn
                  icon
                  color="primary"
                  class="mb-1 ml-2 send-btn"
                  :class="{ 'send-btn-active': (inputText.trim() || selectedImageBase64) && !isTyping }"
                  :disabled="(!inputText.trim() && !selectedImageBase64) || isTyping"
                  @click="sendMessage">
                  <v-icon>mdi-send</v-icon>
                </v-btn>
              </div>
            </div>
          </v-card>
        </div>
      </transition>

      <!-- ============ PRICE DETAIL DIALOG ============ -->
      <v-dialog v-model="priceDialog" max-width="560" overlay-opacity="0.85" transition="dialog-bottom-transition">
        <v-card v-if="activeAbo" dark class="detail-dialog rounded-2xl">
          <div class="detail-header pa-5">
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="detail-title">{{ activeAbo.title }}</div>
                <div class="caption grey--text mt-1">{{ activeAbo.description }}</div>
              </div>
              <v-btn icon @click="priceDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </div>
          </div>
          <v-divider class="grey darken-3"></v-divider>
          <v-card-text class="pa-0">
            <div class="detail-section-title">Verfügbare Laufzeiten</div>
            <div class="variant-list">
              <div v-for="v in activeAbo.variants" :key="v.label + v.price" class="variant-item">
                <div class="d-flex justify-space-between align-center flex-wrap">
                  <div>
                    <span class="white--text font-weight-bold">{{ v.label }}</span>
                    <v-chip v-if="v.isInstallment" x-small color="orange darken-2" class="ml-2">Raten</v-chip>
                  </div>
                  <div class="text-right">
                    <div class="primary--text font-weight-black" style="font-size: 1.15rem">
                      CHF {{ v.price }}<span class="caption grey--text"> gesamt</span>
                    </div>
                    <div class="caption grey--text">≈ CHF {{ v.monthly }}/Mt.</div>
                  </div>
                </div>
                <div v-if="v.signUpFee" class="caption grey--text mt-1">
                  + CHF {{ v.signUpFee }} Aktivierung einmalig
                  <span class="orange--text">(entfällt ab CHF 100 Zusatzpakete)</span>
                </div>
                <div v-if="v.addons && v.addons.length" class="addons-block mt-2">
                  <div class="addons-label">Zusatzpakete (pro Laufzeit):</div>
                  <div v-for="a in v.addons" :key="a.name" class="d-flex justify-space-between addon-row">
                    <span>+ {{ a.name }}</span>
                    <span class="primary--text">CHF {{ a.price }}</span>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
          <v-card-actions class="pa-4">
            <v-btn block color="primary" rounded large :href="activeAbo.contractUrl" target="_blank" class="shadow-glow font-weight-bold">
              <v-icon left>mdi-rocket-launch</v-icon> Jetzt online abschliessen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ============ INFO DETAIL DIALOG ============ -->
      <v-dialog v-model="infoDialog" max-width="520" overlay-opacity="0.85" transition="dialog-bottom-transition">
        <v-card v-if="activeInfo" dark class="detail-dialog rounded-2xl">
          <div class="detail-header pa-5">
            <div class="d-flex justify-space-between align-start">
              <div>
                <div class="detail-title">{{ activeInfo.name }}</div>
                <div class="caption grey--text mt-1">{{ activeInfo.address }}</div>
              </div>
              <v-btn icon @click="infoDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </div>
          </div>
          <v-divider class="grey darken-3"></v-divider>
          <v-card-text class="pa-5">
            <div class="d-flex flex-column mb-4">
              <a :href="'tel:' + activeInfo.phone" class="info-link mb-2">
                <v-icon small color="primary" class="mr-2">mdi-phone-outline</v-icon>{{ activeInfo.phone }}
              </a>
              <a :href="'mailto:' + activeInfo.email" class="info-link">
                <v-icon small color="primary" class="mr-2">mdi-email-outline</v-icon>{{ activeInfo.email }}
              </a>
            </div>
            <v-divider class="grey darken-3 mb-4"></v-divider>
            <div class="white--text font-weight-bold mb-2"><v-icon small color="primary" class="mr-1">mdi-clock-outline</v-icon> Öffnungszeiten</div>
            <div v-for="(block, i) in activeInfo.openingHours" :key="i" class="mb-3">
              <div class="caption grey--text text-uppercase font-weight-bold mb-1">{{ block.title }}</div>
              <div v-for="(h, j) in block.hours" :key="j" class="d-flex justify-space-between caption white--text pl-3 hour-row mb-1">
                <span>{{ h.text }}</span>
                <span class="font-weight-bold primary--text">{{ h.from }} - {{ h.to }}</span>
              </div>
            </div>
            <v-divider class="grey darken-3 mb-4"></v-divider>
            <div class="white--text font-weight-bold mb-2">
              <v-icon small color="primary" class="mr-1">mdi-star-check-outline</v-icon>
              Ausstattung ({{ activeInfo.features.length }})
            </div>
            <div class="d-flex flex-wrap" style="gap: 6px">
              <v-chip v-for="(f, i) in activeInfo.features" :key="i" x-small class="feature-chip">{{ f }}</v-chip>
            </div>
          </v-card-text>
          <v-card-actions class="pa-4 pt-0">
            <v-btn outlined color="primary" rounded :href="activeInfo.mapLink" target="_blank">
              <v-icon left small>mdi-directions</v-icon> Route
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" rounded :href="activeInfo.websiteUrl" target="_blank" class="shadow-glow">
              Webseite <v-icon right small>mdi-open-in-new</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  data() {
    return {
      loadingStudios: true,
      studioList: [],
      search: "",
      selectedCanton: null,
      selectedStudio: null,
      messages: [],
      inputText: "",
      isTyping: false,
      selectedImageBase64: null,
      selectedImagePreview: null,

      // Cache der verfügbaren Widget-Daten (vom Backend einmal pro Request geschickt)
      availableData: { prices: null, info: null, courses: null },

      priceDialog: false,
      activeAbo: null,
      infoDialog: false,
      activeInfo: null,

      quickActions: [
        { icon: "mdi-gift-outline", label: "11 Tage gratis testen", text: "Ich würde gerne erstmal 11 Tage gratis testen." },
        { icon: "mdi-target", label: "Welches Abo passt zu mir?", text: "Welches Abo passt zu mir?" },
        { icon: "mdi-clock-outline", label: "Öffnungszeiten", text: "Wann habt ihr offen?" },
        { icon: "mdi-calendar", label: "Kursplan", text: "Was läuft diese Woche an Kursen?" },
        { icon: "mdi-cash", label: "Was kostet ein Abo?", text: "Was kosten eure Abos?" },
      ],
    }
  },
  async created() {
    await this.fetchStudios()
  },
  computed: {
    filteredStudios() {
      let studios = this.studioList
      if (this.selectedCanton) studios = studios.filter((e) => e.canton === this.selectedCanton)
      if (this.search) {
        const s = this.search.toLowerCase()
        studios = studios.filter((x) => x.name.toLowerCase().includes(s) || (x.adresse && x.adresse.toLowerCase().includes(s)))
      }
      return studios
    },
    studioCantons() {
      return [...new Set(this.studioList.filter((e) => e.canton).map((e) => e.canton))]
    },
  },
  methods: {
    priceCalc(abo, msg) {
      const variant = this.bestVariant(abo, msg)
      if (!variant) return null

      const wanted = (msg && msg.preferredAddons) || []
      const matchedAddons = []

      wanted.forEach((w) => {
        const foundAddon = (variant.addons || []).find((a) => a.name.toLowerCase().includes(w))

        if (foundAddon) {
          const existingAddon = matchedAddons.find((m) => m.originalName === foundAddon.name)
          // Wieder die Regel: Darf das mehrfach gebucht werden?
          const isMultipliable = foundAddon.name.toLowerCase().includes("kinderhort")

          if (existingAddon) {
            // Wenn es existiert UND multiplizierbar ist -> hochzählen
            if (isMultipliable) {
              existingAddon.qty += 1
              existingAddon.name = `${existingAddon.qty}x ${foundAddon.name}`
              existingAddon.price = Number(foundAddon.price) * existingAddon.qty
            }
            // Wenn es existiert aber NICHT multiplizierbar ist (z.B. Milon), passiert einfach nichts (wird ignoriert)
          } else {
            matchedAddons.push({
              ...foundAddon,
              originalName: foundAddon.name,
              qty: 1,
              price: Number(foundAddon.price),
            })
          }
        }
      })

      const price = Number(variant.price) || 0
      const fee = Number(variant.signUpFee) || 0

      const addonSum = matchedAddons.reduce((sum, a) => sum + (Number(a.price) || 0), 0)

      const feeWaived = addonSum >= 100
      const effectiveFee = feeWaived ? 0 : fee
      const total = price + addonSum + effectiveFee

      return { variant, matchedAddons, addonSum, effectiveFee, feeWaived, total, originalFee: fee }
    },
    async fetchStudios() {
      try {
        this.loadingStudios = true
        this.studioList = await api.fetchPublicStudios()
      } catch (e) {
        console.error("Fehler:", e)
      } finally {
        this.loadingStudios = false
      }
    },
    selectStudio(studio) {
      this.selectedStudio = studio
      this.messages = []
    },
    resetStudio() {
      this.selectedStudio = null
    },
    formatMessage(text) {
      if (!text) return ""
      // Komplette Widget-Tags entfernen (werden durch das tatsächliche Widget ersetzt)
      let clean = text.replace(/\[\[WIDGET:[^\]]*\]\]/g, "")
      // Unvollständigen Tag am Ende (während Streaming) ausblenden
      clean = clean.replace(/\[\[[^\]]*$/, "")
      let html = clean.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      html = html.replace(/(https?:\/\/[^\s)]+)/g, '<a href="$1" target="_blank" class="msg-link">$1</a>')
      return html.trim()
    },
    todayDayKey() {
      const today = new Date()
      const weekday = today.toLocaleDateString("de-DE", { weekday: "long" })
      const dd = String(today.getDate()).padStart(2, "0")
      const mm = String(today.getMonth() + 1).padStart(2, "0")
      const yyyy = today.getFullYear()
      return `${weekday}, ${dd}.${mm}.${yyyy}`
    },
    bestVariant(abo, msg) {
      if (!abo || !abo.variants || !abo.variants.length) return null
      // Bevorzugte Laufzeit (aus Tag [[WIDGET:prices:Titel:Laufzeit]])
      if (msg && msg.preferredVariantLabel) {
        const pref = msg.preferredVariantLabel.toLowerCase()
        const match = abo.variants.find((v) => !v.isInstallment && v.label.toLowerCase() === pref)
        if (match) return match
        // Fallback: includes
        const loose = abo.variants.find((v) => !v.isInstallment && v.label.toLowerCase().includes(pref))
        if (loose) return loose
      }
      // Wähle die Variante mit bestem monatlichen Wert (nicht-Raten bevorzugt), fallback 1 Jahr
      const nonInst = abo.variants.filter((v) => !v.isInstallment && v.months > 0)
      const oneYear = nonInst.find((v) => v.months === 12)
      if (oneYear) return oneYear
      if (nonInst.length) return nonInst.reduce((a, b) => (a.monthly < b.monthly ? a : b))
      return abo.variants[0]
    },
    uniqueDays(courses) {
      const days = []
      courses.forEach((k) => {
        const key = `${k.day}, ${k.date.split("-").reverse().join(".")}`
        if (!days.includes(key)) days.push(key)
      })
      return days
    },
    groupedCoursesFiltered(msg) {
      const filter = msg.courseFilter || "all"
      const groups = {}
      msg.widgetData.forEach((k) => {
        const key = `${k.day}, ${k.date.split("-").reverse().join(".")}`
        if (filter !== "all" && filter !== key) return
        if (!groups[key]) groups[key] = []
        groups[key].push(k)
      })
      return groups
    },
    filteredCourseCount(msg) {
      const filter = msg.courseFilter || "all"
      if (filter === "all") return msg.widgetData.length
      return msg.widgetData.filter((k) => `${k.day}, ${k.date.split("-").reverse().join(".")}` === filter).length
    },
    todayHours(info) {
      if (!info || !info.openingHours) return null
      // Heuristik: erste Stunden-Zeile des ersten Blocks (meist "Betreut" oder allg. Öffnungszeit)
      const block = info.openingHours[0]
      if (!block || !block.hours || !block.hours.length) return null
      const h = block.hours[0]
      return `${h.from} - ${h.to}`
    },
    openPriceDetail(abo) {
      this.activeAbo = abo
      this.priceDialog = true
    },
    openInfoDetail(info) {
      this.activeInfo = info
      this.infoDialog = true
    },
    expandPrices(msg) {
      // Wechselt vom Einzel-Empfehlung-View auf alle Abos
      this.$set(msg, "widgetData", msg.widgetAllData)
      this.$set(msg, "expanded", true)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const chatBox = this.$refs.chatBox
        if (chatBox) chatBox.scrollTo({ top: chatBox.scrollHeight, behavior: "smooth" })
      })
    },
    onImageSelected(event) {
      const file = event.target.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (e) => {
        this.selectedImageBase64 = e.target.result
        this.selectedImagePreview = e.target.result
      }
      reader.readAsDataURL(file)
      this.$refs.imageInput.value = ""
    },
    removeSelectedImage() {
      this.selectedImageBase64 = null
      this.selectedImagePreview = null
    },
    sendQuickAction(text) {
      this.inputText = text
      this.sendMessage()
    },

    trialFormValid(data) {
      if (!data) return false
      const phoneOk = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]{6,}$/.test(data.phone || "")
      return !!(data.firstName && data.firstName.trim() && data.lastName && data.lastName.trim() && phoneOk)
    },
    async submitTrialLead(msg) {
      const d = msg.widgetData
      if (!this.trialFormValid(d)) return
      this.$set(d, "loading", true)
      this.$set(d, "error", null)
      try {
        // 1) AI-Notiz aus Chatverlauf generieren (best effort, blockiert nicht)
        let aiNote = ""
        try {
          const histForSummary = this.messages
            .filter((m) => (m.role === "user" || m.role === "assistant") && m.content)
            .map((m) => ({ role: m.role, content: m.content }))
          const res = await api.aiChat.summarizeLead({ history: histForSummary, studioId: this.selectedStudio._id })
          aiNote = (res && res.summary) || ""
        } catch (e) {
          console.warn("Lead-Summary fehlgeschlagen, sende Lead trotzdem:", e)
        }

        // 2) Message zusammenbauen
        const userMsg = (d.message || "").trim()
        const parts = ["Anfrage über Roody Chatbot – 11 Tage gratis testen"]
        if (userMsg) parts.push(`\n📝 Nachricht vom Kunden:\n${userMsg}`)
        if (aiNote) parts.push(`\n🤖 Roody-Notiz (KI-generiert aus Chat):\n${aiNote}`)
        const fullMessage = parts.join("\n")

        // 3) Lead absenden
        const leadEntry = {
          studio: this.selectedStudio._id,
          customer: "5ffc504f26abe65228e36a21",
          leadSetting: "691d90a2e9b1960c3a39a5e9",
          platform: "Roody Chatbot",
          name: d.firstName.trim(),
          surname: d.lastName.trim(),
          phone: d.phone.trim(),
          email: (d.email || "").trim(),
          message: fullMessage,
          status: "Wurde angeworben",
        }
        await api.submitLeadLP({ leadEntry })
        this.$set(d, "submitted", true)

        this.messages.push({
          role: "assistant",
          content: `Mega, ${d.firstName}! 🎉 Ich hab deine Anmeldung weitergegeben. Unser Team meldet sich bei dir, um deinen Start im **${this.selectedStudio.name}** zu organisieren. Bis bald im Studio! 💪`,
        })
        this.scrollToBottom()
      } catch (e) {
        console.error(e)
        this.$set(d, "error", "Ups, das hat nicht geklappt. Bitte versuch es nochmal oder ruf uns an.")
      } finally {
        this.$set(d, "loading", false)
      }
    },
    // ===== Tag-Detection: liest [[WIDGET:...]] aus dem Assistant-Content und setzt das Widget =====
    detectAndApplyWidgetTag(msg) {
      if (msg.widgetType) return // schon gesetzt, nichts tun
      const match = msg.content.match(/\[\[WIDGET:([^\]]+)\]\]/)
      if (!match) return
      const parts = match[1].split(":").map((p) => p.trim())
      const type = parts[0]

      if (type === "prices") {
        if (!this.availableData.prices || !this.availableData.prices.length) return
        const param = parts[1]
        const durationParam = parts[2] // z.B. "2 Jahre", "1 Jahr", "6 Monate"
        if (param) {
          const lower = param.toLowerCase()
          const found = this.availableData.prices.find((p) => p.title.toLowerCase() === lower || p.title.toLowerCase().includes(lower))
          if (found) {
            this.$set(msg, "widgetType", "prices")
            this.$set(msg, "widgetData", [found])
            this.$set(msg, "widgetAllData", this.availableData.prices)
            this.$set(msg, "recommendedTitle", found.title)
            this.$set(msg, "expanded", false)
            if (durationParam) {
              this.$set(msg, "preferredVariantLabel", durationParam)
            }
            // NEU: Addons aus 4. Segment parsen
            if (parts[3]) {
              const addonNames = parts
                .slice(3)
                .join(":")
                .split("|")
                .map((n) => n.trim().toLowerCase())
                .filter(Boolean)
              this.$set(msg, "preferredAddons", addonNames)
            }
            return
          }
        }
        // Alle Abos
        this.$set(msg, "widgetType", "prices")
        this.$set(msg, "widgetData", this.availableData.prices)
        this.$set(msg, "widgetAllData", this.availableData.prices)
        this.$set(msg, "recommendedTitle", null)
        this.$set(msg, "expanded", true)
      } else if (type === "courses") {
        if (!this.availableData.courses || !this.availableData.courses.length) return

        // Suggest-Modus: nur ausgewählte Kurse als Cards
        if (parts[1] === "suggest" && parts[2]) {
          const raw = parts.slice(2).join(":")
          const names = raw
            .split("|")
            .map((n) => n.trim().toLowerCase())
            .filter(Boolean)
          const seen = new Set()
          const matched = []
          names.forEach((name) => {
            const hits = this.availableData.courses.filter((k) => k.courseName.toLowerCase().includes(name))
            hits.slice(0, 3).forEach((h) => {
              if (!seen.has(h.id)) {
                seen.add(h.id)
                matched.push(h)
              }
            })
          })
          if (matched.length) {
            this.$set(msg, "widgetType", "courses")
            this.$set(msg, "widgetMode", "suggestions")
            this.$set(msg, "widgetData", matched)
            this.scrollToBottom()
            return
          }
          // Fallback: kein Match → ganzer Plan
        }

        // Standard: voller Kursplan
        this.$set(msg, "widgetType", "courses")
        this.$set(msg, "widgetMode", "full")
        this.$set(msg, "widgetData", this.availableData.courses)
        const todayKey = this.todayDayKey()
        const hasToday = this.availableData.courses.some((k) => `${k.day}, ${k.date.split("-").reverse().join(".")}` === todayKey)
        this.$set(msg, "courseFilter", hasToday ? todayKey : "all")

        // Legacy: highlight-Modus (behält Kompatibilität zum alten Tag)
        if (parts[1] === "highlight" && parts[2]) {
          const target = parts.slice(2).join(":").toLowerCase()
          const highlight = this.availableData.courses.find((k) => k.courseName.toLowerCase().includes(target))
          if (highlight) {
            this.$set(msg, "highlightCourse", highlight)
            const hKey = `${highlight.day}, ${highlight.date.split("-").reverse().join(".")}`
            this.$set(msg, "courseFilter", hKey)
          }
        }
      } else if (type === "trial") {
        this.$set(msg, "widgetType", "trial")
        this.$set(msg, "widgetData", {
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          message: "",
          submitted: false,
          loading: false,
          error: null,
        })
      } else if (type === "info") {
        if (!this.availableData.info) return
        this.$set(msg, "widgetType", "info")
        this.$set(msg, "widgetData", this.availableData.info)
      }
      this.scrollToBottom()
    },

    async sendMessage() {
      if (!this.inputText.trim() && !this.selectedImageBase64) return
      const userText = this.inputText.trim()
      this.messages.push({ role: "user", content: userText, image: this.selectedImageBase64 })
      this.inputText = ""
      this.removeSelectedImage()
      this.isTyping = true
      this.scrollToBottom()

      const aiMsg = {
        role: "assistant",
        content: "",
        widgetType: null,
        widgetMode: null,
        widgetData: null,
        widgetAllData: null,
        recommendedTitle: null,
        preferredVariantLabel: null,
        highlightCourse: null,
        expanded: false,
        courseFilter: "all",
      }
      const aiIdx = this.messages.push(aiMsg) - 1

      try {
        await api.aiChat.streamChat(
          {
            prompt: userText,
            history: this.messages.slice(0, -1).map((m) => ({ role: m.role, content: m.content })),
            imageBase64: this.selectedImageBase64,
            studioId: this.selectedStudio._id,
          },
          (chunk) => {
            this.isTyping = false
            const lines = chunk.split("\n")
            for (const line of lines) {
              if (!line.startsWith("data: ")) continue
              try {
                const data = JSON.parse(line.replace("data: ", ""))
                // Verfügbare Widget-Daten cachen (kommt einmal am Anfang)
                if (data.available) {
                  this.availableData = data.available
                  continue
                }
                const content = data.choices?.[0]?.delta?.content
                if (content) {
                  this.messages[aiIdx].content += content
                  this.detectAndApplyWidgetTag(this.messages[aiIdx])
                  this.scrollToBottom()
                }
              } catch (e) {}
            }
          },
        )
        // Finaler Check nach Stream-Ende
        this.detectAndApplyWidgetTag(this.messages[aiIdx])
      } catch (error) {
        this.messages[aiIdx].content = "Fehler bei der Verbindung."
      } finally {
        this.isTyping = false
      }
    },
  },
}
</script>

<style scoped>
/* ============ BASE & BG ============ */
.chat-bg {
  background: radial-gradient(ellipse at top, #0d0d12 0%, #050508 100%);
  position: relative;
  overflow: hidden;
}
.relative-z {
  position: relative;
  z-index: 2;
}
.letter-spacing-1 {
  letter-spacing: 1.5px;
}
.smooth-scroll {
  scroll-behavior: smooth;
}

/* Desktop-Container */
.chat-scroll-outer {
  display: flex;
  justify-content: center;
}
.chat-inner {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
}
.widget-wide {
  width: 100%;
  max-width: 820px;
}

.bg-orbs {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
}
.orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.35;
}
.orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #069641, transparent 70%);
  top: -100px;
  left: -100px;
  animation: float1 18s ease-in-out infinite;
}
.orb-2 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #6a1b9a, transparent 70%);
  bottom: -150px;
  right: -150px;
  animation: float2 22s ease-in-out infinite;
}
@keyframes float1 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(80px, 60px) scale(1.15);
  }
}
@keyframes float2 {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }
  50% {
    transform: translate(-100px, -50px) scale(1.1);
  }
}

/* ============ TOOLBAR ============ */
.toolbar-glass {
  backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.avatar-pulse {
  box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.6);
  animation: avatarPulse 2.5s infinite;
}
@keyframes avatarPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.6);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(0, 150, 63, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0);
  }
}
.status-line {
  color: #9aa3b0;
  font-size: 11px;
}
.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4caf50;
  box-shadow: 0 0 8px #4caf50;
  animation: statusBlink 2s infinite;
}
@keyframes statusBlink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
.back-btn {
  transition: transform 0.2s;
}
.back-btn:hover {
  transform: translateX(-3px);
}

/* ============ HELPERS ============ */
.border-highlight {
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.shadow-glow {
  box-shadow: 0 0 18px rgba(0, 150, 63, 0.4) !important;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.rounded-2xl {
  border-radius: 18px !important;
}
.grad-text {
  background: linear-gradient(135deg, #00963f, #10b856);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
::v-deep .msg-link {
  color: #5ffba0;
  text-decoration: underline;
}

/* ============ STUDIO SELECT ============ */
.fade-in-up {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.7s ease forwards;
}
.delay-1 {
  animation-delay: 0.15s;
}
.delay-2 {
  animation-delay: 0.3s;
}
@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.stagger-in {
  opacity: 0;
  animation: fadeInUp 0.5s ease forwards;
}
.canton-chip {
  transition: all 0.25s;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.canton-chip:hover {
  transform: translateY(-2px);
}
.canton-active {
  box-shadow: 0 4px 14px rgba(0, 150, 63, 0.5);
}
.card-glass-dark {
  background: rgba(25, 25, 32, 0.65) !important;
  backdrop-filter: blur(14px);
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  border-radius: 22px !important;
  transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
}
.card-glass-dark:hover {
  transform: translateY(-6px) scale(1.02);
  background: rgba(35, 35, 45, 0.85) !important;
  border-color: rgba(0, 150, 63, 0.5) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.6), 0 0 30px rgba(0, 150, 63, 0.2) !important;
}
.studio-icon-pulse {
  background: radial-gradient(circle, rgba(0, 150, 63, 0.25), rgba(0, 150, 63, 0.05));
  animation: iconPulse 3s infinite;
}
@keyframes iconPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.4);
  }
  50% {
    box-shadow: 0 0 0 10px rgba(0, 150, 63, 0);
  }
}
.custom-search ::v-deep .v-input__slot {
  border-radius: 30px !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(8px);
}

/* ============ CHAT ============ */
.roody-avatar {
  background: radial-gradient(circle, #1a1a22, #0a0a10);
  border: 1px solid rgba(0, 150, 63, 0.4);
  box-shadow: 0 0 12px rgba(0, 150, 63, 0.25);
}
.message-bubble {
  transition: transform 0.2s;
  animation: bubbleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.message-bubble:hover {
  transform: translateY(-1px);
}
@keyframes bubbleIn {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
.user-bubble {
  background: linear-gradient(135deg, #009640, #009640);
  color: white;
  border-bottom-right-radius: 4px !important;
  box-shadow: 0 4px 18px rgba(0, 150, 63, 0.3);
}
.ai-bubble {
  background: rgba(30, 30, 38, 0.85);
  backdrop-filter: blur(10px);
  color: #e8eaed;
  border-bottom-left-radius: 4px !important;
  border: 1px solid rgba(255, 255, 255, 0.06);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}
.message-enter {
  animation: bubbleIn 0.5s ease;
}

.quick-actions {
  display: flex;
  flex-wrap: wrap;
  max-width: 480px;
}
.qa-chip {
  background: rgba(0, 150, 63, 0.12) !important;
  color: #fff !important;
  border: 1px solid rgba(0, 150, 63, 0.3) !important;
  cursor: pointer;
  transition: all 0.25s;
  opacity: 0;
  animation: fadeInUp 0.5s ease forwards;
}
.qa-chip:hover {
  background: rgba(0, 150, 63, 0.25) !important;
  transform: translateY(-2px);
  border-color: #069641 !important;
}

/* Typing */
.typing-enter {
  animation: bubbleIn 0.3s ease;
}
.typing-dot {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #5ffba0, #069641);
  border-radius: 50%;
  animation: typing 1.2s infinite ease-in-out both;
}
.typing-dot:nth-child(1) {
  animation-delay: -0.32s;
}
.typing-dot:nth-child(2) {
  animation-delay: -0.16s;
}
@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.5);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Widget transitions */
.widget-pop-enter-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.widget-pop-enter {
  opacity: 0;
  transform: translateY(20px) scale(0.9);
}
.widget-wrap {
  animation: bubbleIn 0.5s ease;
}
.widget-label {
  font-size: 11px;
  color: #8b96a8;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
  padding-left: 4px;
  display: flex;
  align-items: center;
}

/* ============ PRICE FEATURED (Einzel-Empfehlung) ============ */
.price-card-featured {
  background: linear-gradient(160deg, rgba(40, 50, 75, 0.95), rgba(20, 25, 40, 0.95)) !important;
  border: 1.5px solid rgba(0, 150, 63, 0.6) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 150, 63, 0.2);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}
.price-card-featured:hover {
  transform: translateY(-3px);
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6), 0 0 50px rgba(0, 150, 63, 0.3);
}
.featured-badge-big {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(90deg, #ffa726, #f57c00);
  color: white;
  font-size: 11px;
  font-weight: 900;
  padding: 6px;
  text-align: center;
  letter-spacing: 1.5px;
}
.abo-title-big {
  color: #fff;
  font-weight: 900;
  font-size: 1.6rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 20px;
  margin-bottom: 6px;
}
.featured-variant {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 150, 63, 0.25);
}

/* ============ PRICE COMPACT (Carousel) ============ */
.price-card-compact {
  background: linear-gradient(160deg, rgba(35, 35, 45, 0.95), rgba(20, 20, 28, 0.95)) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;
}
.price-card-compact::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 0%, rgba(0, 150, 63, 0.15), transparent 70%);
  opacity: 0;
  transition: opacity 0.4s;
}
.price-card-compact:hover {
  transform: translateY(-6px);
  border-color: rgba(0, 150, 63, 0.6) !important;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.5), 0 0 25px rgba(0, 150, 63, 0.2);
}
.price-card-compact:hover::before {
  opacity: 1;
}
.price-card-compact.featured {
  border: 1px solid rgba(0, 150, 63, 0.55) !important;
  box-shadow: 0 0 30px rgba(0, 150, 63, 0.2);
}
.featured-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  background: linear-gradient(135deg, #ffa726, #f57c00);
  color: white;
  font-size: 10px;
  font-weight: 900;
  padding: 4px 10px;
  border-radius: 20px;
  box-shadow: 0 3px 10px rgba(245, 124, 0, 0.4);
  animation: badgeFloat 2.5s ease-in-out infinite;
}
@keyframes badgeFloat {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}
.abo-title {
  color: #069641;
  font-weight: 900;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.abo-desc {
  color: #9aa3b0;
  font-size: 12px;
  line-height: 1.4;
  min-height: 50px;
}
.abo-price-wrap {
  display: flex;
  align-items: baseline;
  gap: 4px;
}
.abo-from {
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
}
.abo-price {
  color: #fff;
  font-size: 1.8rem;
  font-weight: 900;
  background: linear-gradient(135deg, #fff, #b6ffd4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.abo-unit {
  color: #9aa3b0;
  font-size: 11px;
}
.abo-cta-hint {
  margin-top: 10px;
  font-size: 10px;
  color: #069641;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* ============ INFO COMPACT ============ */
.info-card-compact {
  background: linear-gradient(160deg, rgba(30, 30, 38, 0.95), rgba(15, 15, 22, 0.95)) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  cursor: pointer;
  transition: all 0.3s;
  overflow: hidden;
}
.info-card-compact:hover {
  transform: translateY(-3px);
  border-color: rgba(0, 150, 63, 0.5) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.4);
}
.info-header-compact {
  background: linear-gradient(120deg, rgba(0, 150, 63, 0.25), rgba(0, 0, 0, 0.4));
  background-size: 200% 200%;
  animation: gradientShift 8s ease infinite;
}
@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}
.info-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
}
.info-tap-hint {
  text-align: center;
  padding: 8px;
  font-size: 10px;
  color: #069641;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}
.info-link {
  text-decoration: none;
  color: #c5c9d1;
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 8px;
  transition: all 0.2s;
}
.info-link:hover {
  background: rgba(0, 150, 63, 0.1);
  color: #fff;
  transform: translateX(3px);
}
.hour-row {
  border-left: 2px solid rgba(0, 150, 63, 0.4);
  padding: 4px 0 4px 10px;
}
.feature-chip {
  background: rgba(0, 150, 63, 0.12) !important;
  color: #b0bec5 !important;
  border: 1px solid rgba(0, 150, 63, 0.2) !important;
}
.feature-chip-mini {
  background: rgba(0, 150, 63, 0.12) !important;
  color: #b0bec5 !important;
  border: 1px solid rgba(0, 150, 63, 0.2) !important;
  font-size: 10px !important;
}

/* ============ COURSE WIDGET ============ */
.course-card {
  background: linear-gradient(160deg, rgba(30, 30, 38, 0.95), rgba(15, 15, 22, 0.95)) !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.highlight-course-card {
  background: linear-gradient(120deg, rgba(255, 167, 38, 0.15), rgba(0, 150, 63, 0.1));
  border: 1px solid rgba(255, 167, 38, 0.4);
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 8px;
}
.highlight-label {
  color: #ffa726;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.day-filter-chip {
  cursor: pointer;
  transition: all 0.2s;
}
.day-filter-chip:hover {
  transform: translateY(-1px);
}
.course-scroll {
  max-height: 460px;
  overflow-y: auto;
  padding: 0 16px 16px;
}
.course-day-group {
  margin-bottom: 14px;
}
.day-header {
  position: sticky;
  top: 0;
  background: rgba(15, 15, 22, 0.95);
  backdrop-filter: blur(8px);
  color: #069641;
  font-weight: 800;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 150, 63, 0.2);
  margin-bottom: 6px;
  z-index: 1;
}
.course-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 8px;
  border-radius: 10px;
  transition: all 0.25s;
  border-left: 2px solid transparent;
}
.course-row:hover {
  background: rgba(0, 150, 63, 0.1);
  border-left-color: #069641;
  transform: translateX(4px);
}
.course-row-highlight {
  background: rgba(255, 167, 38, 0.08);
  border-left-color: #ffa726 !important;
}
.course-time {
  color: #069641;
  font-weight: 800;
  font-size: 14px;
  min-width: 50px;
}
.course-info {
  flex: 1;
}

/* ============ SUGGESTION CARDS (kuratierte Kursvorschläge) ============ */
.suggestion-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.suggestion-card {
  background: linear-gradient(135deg, rgba(255, 167, 38, 0.08), rgba(0, 150, 63, 0.06));
  border: 1px solid rgba(255, 167, 38, 0.25);
  border-radius: 14px;
  padding: 14px 16px;
  transition: all 0.25s;
}
.suggestion-card:hover {
  border-color: rgba(255, 167, 38, 0.5);
  transform: translateX(3px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
}
.suggestion-title {
  color: #fff;
  font-weight: 900;
  font-size: 1.05rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.suggestion-time-chip {
  color: #fff !important;
  font-weight: 700 !important;
}
.suggestion-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  color: #b0bec5;
  font-size: 12px;
  margin-bottom: 8px;
}
.suggestion-desc {
  color: #9aa3b0;
  font-size: 12px;
  line-height: 1.5;
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}

/* ============ DETAIL DIALOG ============ */
.detail-dialog {
  background: linear-gradient(180deg, rgba(25, 25, 32, 0.98), rgba(12, 12, 18, 0.98)) !important;
  border: 1px solid rgba(0, 150, 63, 0.3) !important;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.7), 0 0 60px rgba(0, 150, 63, 0.15) !important;
}
.detail-header {
  background: linear-gradient(120deg, rgba(0, 150, 63, 0.2), transparent);
}
.detail-title {
  font-size: 1.5rem;
  font-weight: 900;
  color: #069641;
  text-transform: uppercase;
  letter-spacing: 1px;
}
.detail-price {
  font-size: 2rem;
  font-weight: 900;
  background: linear-gradient(135deg, #fff, #5ffba0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
.detail-section-title {
  padding: 16px 20px 8px;
  color: #8b96a8;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
}
.variant-list {
  padding: 0 16px 8px;
}
.variant-item {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
  transition: all 0.25s;
}
.variant-item:hover {
  background: rgba(0, 150, 63, 0.08);
  border-color: rgba(0, 150, 63, 0.3);
}
.addons-block {
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
}
.addons-label {
  font-size: 10px;
  color: #8b96a8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.addon-row {
  font-size: 12px;
  color: #c5c9d1;
  padding: 3px 0;
}

/* ============ INPUT BAR ============ */
.input-bar {
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.06) !important;
}
.custom-chat-input ::v-deep .v-input__slot {
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s;
}
.custom-chat-input ::v-deep .v-input__slot:hover {
  border-color: rgba(0, 150, 63, 0.4);
}
.image-btn {
  transition: transform 0.2s;
}
.image-btn:hover {
  transform: scale(1.1) rotate(-5deg);
}
.send-btn {
  transition: all 0.3s;
}
.send-btn-active {
  background: rgba(0, 150, 63, 0.15) !important;
  box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.6);
  animation: sendPulse 2s infinite;
}
@keyframes sendPulse {
  0% {
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0.5);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(0, 150, 63, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(0, 150, 63, 0);
  }
}
.remove-img-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  background-color: #1e1e1e !important;
}

/* ============ TRANSITIONS ============ */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s ease;
}
.fade-slide-enter {
  opacity: 0;
  transform: translateY(20px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* ============ TRIAL CARD ============ */
.trial-card {
  background: linear-gradient(160deg, rgba(40, 30, 15, 0.95), rgba(20, 15, 10, 0.95)) !important;
  border: 1.5px solid rgba(255, 167, 38, 0.5) !important;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 40px rgba(255, 167, 38, 0.15);
}
.trial-header {
  background: linear-gradient(120deg, rgba(255, 167, 38, 0.2), transparent);
}
.trial-title {
  color: #ffa726;
  font-weight: 900;
  font-size: 1.05rem;
  letter-spacing: 1px;
}
</style>
